import { addDays } from 'date-fns'
import isNil from 'lodash/isNil'
import { useCallback } from 'react'

import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'

export const useUpdateDependentDates = () => {
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { isQuote } = useInvoiceEditorDocumentType()
  const { getValues, setValue } = invoiceEditorForm

  // On a quote `dueDate` is the quote's own expiry date, while `paymentTerm` only describes the
  // conditions of the future invoice, so the two must stay independent.
  const shouldDeriveDueDateFromPaymentTerm = !isQuote

  const handleRecurringInvoiceFirstInvoiceDateChange = useCallback(
    (firstInvoiceDate?: Date | null) => {
      if (isNil(firstInvoiceDate)) {
        return
      }

      setValue('issueDate', firstInvoiceDate)
    },
    [setValue],
  )

  const handleIssueDateChange = useCallback(
    (issueDate?: Date | null) => {
      if (isNil(issueDate)) {
        return
      }

      const selectedPaymentTerm = getValues('paymentTerm')
      if (shouldDeriveDueDateFromPaymentTerm && selectedPaymentTerm >= 0) {
        setValue('dueDate', addDays(issueDate, selectedPaymentTerm))
      }
    },
    [getValues, setValue, shouldDeriveDueDateFromPaymentTerm],
  )

  const handlePaymentTermChange = useCallback(
    (paymentTerm?: number) => {
      if (isNil(paymentTerm)) {
        return
      }

      const existingIssueDate = getValues('issueDate')
      if (shouldDeriveDueDateFromPaymentTerm && existingIssueDate) {
        setValue('dueDate', addDays(existingIssueDate, paymentTerm))
      }
    },
    [getValues, setValue, shouldDeriveDueDateFromPaymentTerm],
  )

  return {
    handleIssueDateChange,
    handlePaymentTermChange,
    handleRecurringInvoiceFirstInvoiceDateChange,
  }
}
